import type { Product } from '@/domain/Product';
import useTranslations from '@/widgets/hooks/useTranslations';
import ProductImage from '@/widgets/ProductList/cards/parts/ProductImage/ProductImage';
import type { Translations } from '@/widgets/ProductList/config';
import styles from './BundleProduct.module.scss';
import SkeletonShape from '@/widgets/components/controls/Skeleton/SkeletonShape';
import SkeletonText from '@/widgets/components/controls/Skeleton/SkeletonText';

export type BundleProductProps = {
  product: Product;
  quantityInBundle: number;
};
export default function BundleProduct({
  product,
  quantityInBundle
}: BundleProductProps) {
  const { t } = useTranslations<Translations>();
  const { name, headline, mainImage, salesMultiple, unitQuantity, type } =
    product;
  const numberOfSleeves = unitQuantity * quantityInBundle;
  const numberOfCapsules = salesMultiple * quantityInBundle;

  const sleeveLabel = t(
    'productList.quickView.assortments.sleeveCapsulesLabel',
    {
      count: numberOfSleeves,
      sleeve: numberOfSleeves,
      capsules: numberOfCapsules
    }
  );
  const quantityLabel = t('productList.quickView.bundles.itemQuantity', {
    count: quantityInBundle,
    quantity: quantityInBundle
  });

  return (
    <li
      class={styles.listItem}
      data-product-long-sku={product.id}
      data-product-short-sku={product?.nessoft?.legacyId}
    >
      <div class={styles.imageWrapper}>
        <ProductImage
          image={mainImage}
          width={88}
          imagePolicy={{
            policyId: 'small',
            targetWidth: 88,
            density: 1
          }}
          class={styles.productImage}
          loading="lazy"
        />
      </div>
      <div class={styles.itemContent}>
        <h3 class={styles.productName}>{name}</h3>
        <p class={styles.productHeadline}>{headline}</p>
        <p class={styles.quantities}>
          {type === 'capsule' ? sleeveLabel : quantityLabel}
        </p>
      </div>
    </li>
  );
}

BundleProduct.Skeleton = function Skeleton() {
  return (
    <li class={styles.listItem}>
      <div class={styles.imageWrapper}>
        <SkeletonShape class={styles.skeletonStyle} />
      </div>
      <SkeletonText class={styles.skeletonStyle} />
    </li>
  );
};

BundleProduct.Outline = function Outline({ label }: { label: string }) {
  return (
    <li class={styles.listItem}>
      <div class={styles.imageWrapper}>
        <CapsuleOutlineImage />
      </div>

      <div class={styles.outlineText}>{label}</div>
    </li>
  );
};

function CapsuleOutlineImage() {
  return (
    <svg
      width="92"
      height="72"
      viewBox="0 0 92 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={styles.outline}
    >
      <path
        // eslint-disable-next-line max-len
        d="M45.9846 72H1.44999C1.06543 72 0.696618 71.8435 0.424692 71.565C0.152766 71.2866 0 70.9088 0 70.515V70.473C-2.38687e-06 70.0787 0.1526 69.7004 0.424379 69.4211C0.696159 69.1417 1.06496 68.9841 1.44999 68.9827H8.07997C8.25478 68.9834 8.424 68.9197 8.55666 68.8031C8.68932 68.6865 8.77655 68.5248 8.80241 68.3478L16.6108 13.339C16.7072 12.6927 16.9407 12.076 17.2947 11.532C17.6488 10.9881 18.1149 10.5302 18.6603 10.1905L33.9287 0.65593C34.6115 0.226386 35.3972 -0.000682524 36.1985 1.54105e-06H55.7964C56.5976 0.000139142 57.3831 0.227146 58.0662 0.65593L73.3346 10.1905C73.8809 10.5291 74.3477 10.9867 74.7019 11.5308C75.0561 12.075 75.289 12.6923 75.384 13.339L83.1976 68.3478C83.2223 68.5244 83.3085 68.6859 83.4403 68.8026C83.5721 68.9193 83.7407 68.9833 83.9149 68.9827H90.5449C90.9308 68.9827 91.3009 69.1397 91.5738 69.4192C91.8467 69.6987 92 70.0778 92 70.473V70.515C91.9986 70.9093 91.8447 71.287 91.572 71.5654C91.2992 71.8437 90.9299 72 90.5449 72H45.9846Z"
        fill="#E7E7E7"
      />
    </svg>
  );
}
